class EbHeader extends HTMLElement {
  connectedCallback() {
    this.lastScrollY = window.scrollY;
    this.openPanel = null;
    this.drawer = this.querySelector('.eb-nav-drawer');
    this.scrim = this.querySelector('.eb-header__scrim');
    this.boundCartCountUpdate = (event) => {
      const detail = event.detail || {};
      if (event.type === 'eb:cart:open' && !detail.item && !detail.payload && !(detail.lines || []).length) return;
      if (event.type === 'eb:cart:add') this.playCartAddMotion(detail);
      this.refreshCartCount(detail);
    };

    this.bindMegaMenu();
    this.bindDrawer();
    this.bindSearch();
    this.bindLocalization();
    this.bindCart();
    this.bindSticky();
    document.addEventListener('eb:cart:add', this.boundCartCountUpdate);
    document.addEventListener('eb:cart:open', this.boundCartCountUpdate);
    document.addEventListener('eb:cart:updated', this.boundCartCountUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('eb:cart:add', this.boundCartCountUpdate);
    document.removeEventListener('eb:cart:open', this.boundCartCountUpdate);
    document.removeEventListener('eb:cart:updated', this.boundCartCountUpdate);
  }

  bindMegaMenu() {
    this.bindMegaProducts();

    this.querySelectorAll('[data-eb-mega-trigger]').forEach((trigger) => {
      const panel = this.querySelector(`#${trigger.getAttribute('aria-controls')}`);
      if (!panel) return;

      let enterTimer;
      let leaveTimer;
      const closeDelay = 160;
      const open = () => {
        clearTimeout(leaveTimer);
        enterTimer = window.setTimeout(() => this.openMega(trigger, panel), 120);
      };
      const close = (event) => {
        const nextTarget = event?.relatedTarget;
        if (nextTarget instanceof Node && (panel.contains(nextTarget) || trigger.contains(nextTarget))) return;

        clearTimeout(enterTimer);
        leaveTimer = window.setTimeout(() => this.closeMega(), closeDelay);
      };

      trigger.addEventListener('mouseenter', open);
      panel.addEventListener('mouseenter', () => clearTimeout(leaveTimer));
      panel.addEventListener('mouseleave', close);
      trigger.addEventListener('click', () => {
        if (trigger.getAttribute('aria-expanded') === 'true') {
          this.closeMega();
        } else {
          this.openMega(trigger, panel);
        }
      });
      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
          event.preventDefault();
          this.openMega(trigger, panel);
          panel.querySelector('a, button')?.focus();
        }
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeMega(true);
        if (this.classList.contains('is-drawer-open')) this.closeDrawer();
      }
    });
  }

  bindMegaProducts() {
    this.querySelectorAll('[data-eb-mega-grid]').forEach((grid) => {
      const scenarios = [...grid.querySelectorAll('[data-eb-mega-scenario]')];
      const panes = [...grid.querySelectorAll('[data-eb-mega-products]')];
      if (!scenarios.length || !panes.length) return;

      const showPane = (index) => {
        if (!panes.some((pane) => pane.dataset.ebMegaProducts === index)) return;

        scenarios.forEach((scenario) => {
          scenario.classList.toggle('is-active', scenario.dataset.ebMegaScenario === index);
        });
        panes.forEach((pane) => {
          const isActive = pane.dataset.ebMegaProducts === index;
          pane.classList.toggle('is-active', isActive);
          pane.hidden = !isActive;
        });
      };

      scenarios.forEach((scenario) => {
        const index = scenario.dataset.ebMegaScenario;
        scenario.addEventListener('mouseenter', () => showPane(index));
        scenario.addEventListener('focus', () => showPane(index));
      });
    });
  }

  openMega(trigger, panel) {
    this.closeMega();
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    this.openPanel = { trigger, panel };
  }

  closeMega(restoreFocus = false) {
    this.querySelectorAll('[data-eb-mega-trigger]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    this.querySelectorAll('[data-eb-menu-panel]').forEach((panel) => {
      panel.hidden = true;
    });
    if (restoreFocus) this.openPanel?.trigger?.focus();
    this.openPanel = null;
  }

  bindDrawer() {
    this.querySelectorAll('[data-eb-drawer-open]').forEach((button) => {
      button.addEventListener('click', () => this.openDrawer(button));
    });
    this.querySelectorAll('[data-eb-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeDrawer());
    });
    this.querySelectorAll('[data-eb-drawer-next]').forEach((button) => {
      button.addEventListener('click', () => this.openDrawerLevel(button.dataset.ebDrawerNext, button));
    });
    this.querySelectorAll('[data-eb-drawer-back]').forEach((button) => {
      button.addEventListener('click', () => this.closeDrawerLevel(button));
    });

    this.drawer?.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') this.trapFocus(event);
      if (event.key === 'Escape') {
        const activeLevel = this.drawer.querySelector('.eb-nav-drawer__level--secondary.is-active');
        if (activeLevel) {
          this.closeDrawerLevel(activeLevel.querySelector('[data-eb-drawer-back]'));
        } else {
          this.closeDrawer();
        }
      }
    });
  }

  openDrawer(button) {
    this.drawerTrigger = button;
    this.classList.add('is-drawer-open');
    this.drawer?.setAttribute('aria-hidden', 'false');
    if (this.scrim) this.scrim.hidden = false;
    document.body.classList.add('eb-drawer-open');
    document.getElementById('MainContent')?.setAttribute('inert', '');
    button.setAttribute('aria-expanded', 'true');
    this.resetDrawerHorizontalScroll(this.drawer);
    window.setTimeout(() => {
      this.resetDrawerHorizontalScroll(this.drawer);
      this.drawer?.querySelector('button, a, select')?.focus();
    }, 30);
  }

  closeDrawer() {
    this.classList.remove('is-drawer-open');
    this.drawer?.setAttribute('aria-hidden', 'true');
    this.drawer?.classList.remove('is-secondary');
    this.drawer?.querySelectorAll('.eb-nav-drawer__level--secondary').forEach((panel) => {
      panel.hidden = true;
      panel.classList.remove('is-active');
    });
    if (this.scrim) this.scrim.hidden = true;
    document.body.classList.remove('eb-drawer-open');
    document.getElementById('MainContent')?.removeAttribute('inert');
    this.querySelectorAll('[data-eb-drawer-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    this.resetDrawerHorizontalScroll(this.drawer);
    this.drawerTrigger?.focus();
  }

  openDrawerLevel(id, trigger) {
    const panel = this.querySelector(`#${id}`);
    if (!panel) return;
    this.drawerLevelTrigger = trigger;
    this.drawer?.classList.add('is-secondary');
    panel.hidden = false;
    this.resetDrawerHorizontalScroll(panel);
    requestAnimationFrame(() => panel.classList.add('is-active'));
    window.setTimeout(() => {
      this.resetDrawerHorizontalScroll(panel);
      panel.querySelector('button, a')?.focus();
    }, 30);
  }

  closeDrawerLevel(button) {
    const panel = button?.closest('.eb-nav-drawer__level--secondary');
    if (!panel) return;
    panel.classList.remove('is-active');
    this.drawer?.classList.remove('is-secondary');
    window.setTimeout(() => {
      panel.hidden = true;
      this.drawerLevelTrigger?.focus();
    }, 240);
  }

  resetDrawerHorizontalScroll(root) {
    if (!root) return;
    root.scrollLeft = 0;
    root.querySelectorAll('*').forEach((node) => {
      if (node.scrollLeft) node.scrollLeft = 0;
    });
  }

  trapFocus(event) {
    const focusable = [...this.drawer.querySelectorAll('a[href], button:not([disabled]), select:not([disabled]), textarea:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((node) => node.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  bindSearch() {
    this.querySelectorAll('[data-eb-search-trigger]').forEach((button) => {
      button.addEventListener('click', () => {
        this.dispatchEvent(new CustomEvent('eb:search:open', { bubbles: true }));
        this.closeDrawer();
      });
    });
  }

  bindLocalization() {
    this.querySelectorAll('.eb-localization select').forEach((select) => {
      select.addEventListener('change', () => select.form?.submit());
    });
  }

  bindCart() {
    this.querySelectorAll('[data-eb-cart-trigger]').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        document.dispatchEvent(new CustomEvent('eb:cart:open', { detail: { lines: [] } }));
      });
    });
  }

  refreshCartCount(detail = {}) {
    window.clearTimeout(this.cartCountTimer);
    this.cartCountTimer = window.setTimeout(() => {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw response;
          return response.json();
        })
        .then((cart) => this.setCartCount(Number(cart.item_count || 0)))
        .catch(() => this.incrementCartCount(detail?.lines || []));
    }, 80);
  }

  incrementCartCount(lines = []) {
    const quantity = lines.reduce((total, line) => total + Number(line.quantity || 1), 0);
    if (!quantity) return;
    const current = Number(this.querySelector('[data-eb-cart-count]')?.textContent || 0);
    this.setCartCount(current + quantity);
  }

  setCartCount(count) {
    this.querySelectorAll('[data-eb-cart-count]').forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = count <= 0;
      badge.classList.remove('is-bumping');
      void badge.offsetWidth;
      if (count > 0) badge.classList.add('is-bumping');
    });
  }

  playCartAddMotion(detail = {}) {
    const cartLinks = this.visibleCartLinks();
    cartLinks.forEach((link) => this.bumpCartLink(link));
    const target = cartLinks[0];
    if (target && detail.sourceRect) this.playCartFlyer(detail.sourceRect, target.getBoundingClientRect());
  }

  visibleCartLinks() {
    return [...this.querySelectorAll('.eb-header__cart')]
      .filter((link) => {
        const rect = link.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      });
  }

  bumpCartLink(link) {
    link.classList.remove('is-cart-adding');
    void link.offsetWidth;
    link.classList.add('is-cart-adding');
    window.clearTimeout(link.ebCartMotionTimer);
    link.ebCartMotionTimer = window.setTimeout(() => link.classList.remove('is-cart-adding'), 620);
  }

  playCartFlyer(sourceRect, targetRect) {
    const startX = Number(sourceRect.left || 0) + Number(sourceRect.width || 0) / 2;
    const startY = Number(sourceRect.top || 0) + Number(sourceRect.height || 0) / 2;
    const endX = targetRect.left + targetRect.width / 2;
    const endY = targetRect.top + targetRect.height / 2;
    const flyer = document.createElement('span');
    flyer.className = 'eb-cart-flyer';
    flyer.setAttribute('aria-hidden', 'true');
    flyer.style.left = `${startX}px`;
    flyer.style.top = `${startY}px`;
    flyer.style.setProperty('--eb-cart-flyer-x', `${endX - startX}px`);
    flyer.style.setProperty('--eb-cart-flyer-y', `${endY - startY}px`);
    document.body.appendChild(flyer);
    flyer.addEventListener('animationend', () => flyer.remove(), { once: true });
  }

  bindSticky() {
    const behavior = this.dataset.sticky;
    if (behavior === 'none') return;

    const update = () => {
      const currentY = window.scrollY;
      this.classList.toggle('is-scrolled', currentY > 24);

      if (behavior === 'scroll-up') {
        this.classList.toggle('is-hidden', currentY > this.lastScrollY && currentY > 120);
      }

      this.lastScrollY = currentY;
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
  }
}

if (!customElements.get('eb-header')) {
  customElements.define('eb-header', EbHeader);
}
